/**
 * WP Consent API: updateWPConsent, scheduleWpConsentRetry.
 * @package AdvancedTrackingManager
 */

import { createLogger } from '@lib/logger';
import { getCookie } from '@lib/cookies';

const w = globalThis as unknown as Window;

const pendingWpConsentUpdates: Record<string, string> = {};
let wpConsentRetryInterval: ReturnType<typeof setInterval> | null = null;

export const updateWPConsent = (
  category: string,
  value: string,
  config: { turnstileConfigured?: boolean; debugMode?: boolean },
  opts?: { silent?: boolean }
): void => {
  const normalizedValue = value === 'allow' ? 'allow' : 'deny';
  const silent = opts?.silent === true;
  const log = createLogger('ATM Consent Handler', config.debugMode ?? false);

  if (typeof w.wp_set_consent !== 'function') {
    log.debug('⚠️ WP Consent API not available - queuing update', {
      category,
      value: normalizedValue,
    });
    pendingWpConsentUpdates[category] = normalizedValue;
    scheduleWpConsentRetry(config);
    return;
  }

  try {
    w.wp_set_consent(category, normalizedValue);
    if (!silent) {
      log.debug('✅ Updated WP Consent API', { category, value: normalizedValue });
    }

    const cookieName = `wp_consent_${category}`;
    const cookieValue = getCookie(cookieName);
    if (cookieValue === normalizedValue) {
      if (!silent) {
        log.debug('✅ Verified WP Consent API cookie immediately', {
          category,
          value: cookieValue,
        });
      }
    } else if (cookieValue !== null) {
      log.debug('⚠️ WP Consent API cookie mismatch - retrying', {
        category,
        expected: normalizedValue,
        actual: cookieValue,
      });
      setTimeout(() => {
        w.wp_set_consent?.(category, normalizedValue);
      }, 50);
    } else {
      log.debug('⚠️ WP Consent API cookie not found - will retry', {
        category,
        value: normalizedValue,
      });
      pendingWpConsentUpdates[category] = normalizedValue;
      scheduleWpConsentRetry(config);
      setTimeout(() => {
        if (typeof w.wp_set_consent === 'function') {
          w.wp_set_consent(category, normalizedValue);
          log.debug('🔄 Retried WP Consent API update', { category, value: normalizedValue });
        }
      }, 100);
    }
  } catch (error) {
    log.debug('⚠️ Failed to update WP Consent API immediately', {
      category,
      value: normalizedValue,
      error,
    });
    pendingWpConsentUpdates[category] = normalizedValue;
    scheduleWpConsentRetry(config);
  }
};

export const scheduleWpConsentRetry = (config: { debugMode?: boolean }): void => {
  if (wpConsentRetryInterval) return;

  const log = createLogger('ATM Consent Handler', config.debugMode ?? false);

  wpConsentRetryInterval = setInterval(() => {
    if (typeof w.wp_set_consent !== 'function') return;

    const pendingCategories = Object.keys(pendingWpConsentUpdates);
    if (pendingCategories.length === 0) {
      clearInterval(wpConsentRetryInterval!);
      wpConsentRetryInterval = null;
      return;
    }

    for (const category of pendingCategories) {
      const pendingValue = pendingWpConsentUpdates[category];
      if (pendingValue === undefined) continue;
      try {
        w.wp_set_consent(category, pendingValue);
        log.debug('✅ Applied queued WP Consent API update', { category, value: pendingValue });
        delete pendingWpConsentUpdates[category];
      } catch (error) {
        log.debug('⚠️ Failed to apply queued WP Consent API update', {
          category,
          value: pendingValue,
          error,
        });
      }
    }

    if (Object.keys(pendingWpConsentUpdates).length === 0) {
      clearInterval(wpConsentRetryInterval!);
      wpConsentRetryInterval = null;
    }
  }, 250);
};
